
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle, ArrowRight, Shield, Bell, Map, AlertTriangle, Users } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

const UpgradePage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleUpgrade = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    setIsProcessing(true);
    
    try {
      toast({
        title: "Processing...",
        description: "Connecting to Stripe checkout...",
      });
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { },
      });

      if (error) {
        console.error('Stripe checkout error:', error);
        throw error;
      }
      
      if (!data?.url) {
        console.error('No checkout URL returned:', data);
        throw new Error('No checkout URL returned from the server');
      }

      console.log('Redirecting to Stripe checkout URL:', data.url);
      window.location.href = data.url;
    } catch (error) {
      console.error('Error during checkout process:', error);
      toast({
        title: "Checkout Failed",
        description: "There was an error starting the checkout process. Please try again.",
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1 py-12">
        <div className="container max-w-5xl">
          <div className="mb-12 text-center">
            <h1 className="text-4xl font-bold mb-4">Upgrade to Vigilante</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get exclusive access to premium safety features and help make your community safer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 border-okkio-orange">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-2xl">Vigilante Membership</CardTitle>
                  <Shield className="h-8 w-8 text-okkio-orange" />
                </div>
                <CardDescription>Premium safety features</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <p className="text-3xl font-bold">€4.99<span className="text-lg text-gray-500">/month</span></p>
                  <p className="text-sm text-gray-500">Cancel anytime</p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <CheckCircle className="mr-3 h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>All Citizen features included</span>
                  </div>
                  <div className="flex items-start">
                    <Bell className="mr-3 h-5 w-5 text-okkio-orange flex-shrink-0" />
                    <span>Real-time crime alerts in a 500m radius</span>
                  </div>
                  <div className="flex items-start">
                    <AlertTriangle className="mr-3 h-5 w-5 text-okkio-orange flex-shrink-0" />
                    <span>Alerts for high crime rate areas</span>
                  </div>
                  <div className="flex items-start">
                    <Users className="mr-3 h-5 w-5 text-okkio-orange flex-shrink-0" />
                    <span>Proximity alerts to nearby Okkio users</span>
                  </div>
                  <div className="flex items-start">
                    <Map className="mr-3 h-5 w-5 text-okkio-orange flex-shrink-0" />
                    <span>Enhanced crime map with detailed analytics</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  onClick={handleUpgrade} 
                  className="w-full bg-okkio-orange hover:bg-okkio-orange-600"
                  disabled={isProcessing}
                >
                  {isProcessing ? "Processing..." : "Upgrade Now"} 
                  {!isProcessing && <ArrowRight className="ml-2 h-5 w-5" />}
                </Button>
              </CardFooter>
            </Card>

            <div className="flex flex-col justify-center space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold mb-2 flex items-center">
                  <Bell className="mr-2 h-5 w-5 text-okkio-orange" />
                  Real-time Crime Alerts
                </h3>
                <p className="text-gray-700">
                  Receive instant notifications about crimes reported within 500 meters of your location, 
                  allowing you to stay informed about safety concerns in your immediate vicinity.
                </p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold mb-2 flex items-center">
                  <AlertTriangle className="mr-2 h-5 w-5 text-okkio-orange" />
                  High Crime Rate Alerts
                </h3>
                <p className="text-gray-700">
                  Get notified when you enter areas with historically high crime rates, 
                  helping you make informed decisions about travel and safety.
                </p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold mb-2 flex items-center">
                  <Users className="mr-2 h-5 w-5 text-okkio-orange" />
                  Proximity Alerts
                </h3>
                <p className="text-gray-700">
                  Know when other Okkio users are nearby, creating a network of community safety 
                  and allowing for potential assistance in unsafe situations.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <h3 className="text-xl font-bold mb-2">100% Satisfaction Guarantee</h3>
            <p className="text-gray-700">
              If you're not completely satisfied with your Vigilante membership, 
              you can cancel anytime within the first 30 days for a full refund.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UpgradePage;
